import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const ProjectCard = ({ project }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Extract image URLs from description blocks
  const descriptionImages = project.description
    ?.filter((block) => block.type === "image" && block.url)
    .map((block) => block.url) || [];

  // Check if preview is a GIF
  const isGif = project.preview?.toLowerCase().endsWith(".gif");

  // Get media to display (GIF or description images)
  const hasDynamicMedia = isGif || descriptionImages.length > 0;
  const mediaItems = isGif ? [project.preview] : descriptionImages;

  // Cycle through images every 1 second when hovered and multiple images exist
  useEffect(() => {
    if (!isHovered || mediaItems.length <= 1) return;

    const timer = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % mediaItems.length);
    }, 2000);

    return () => clearInterval(timer);
  }, [isHovered, mediaItems.length]);

  return (
    <motion.div
      className="relative overflow-hidden rounded-3xl shadow-lg bg-neutral-100 dark:bg-neutral-800 max-w-md mx-auto w-full z-10"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.6 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setCurrentImageIndex(0); // Reset to first image
      }}
    >
      {/* Media Section */}
      <div className="relative h-80 w-full">
        {/* Static Image */}
        <motion.div
          className="absolute top-0 left-0 w-full h-full overflow-hidden"
          initial={{ opacity: 0, scale: 1 }}
          animate={isHovered && hasDynamicMedia ? { opacity: 0, scale: 1.1 } : { opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          {/* Background image with gray tint */}
          <div
            className="w-full h-full"
            style={{
              backgroundImage: `url(${project.logo_background})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'brightness(0.7)' // Applies a gray tint
            }}
          />
          {/* Logo on top */}
          <img
            src={project.logo}
            alt={project.title}
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-24 object-contain"
          />
        </motion.div>

        {/* Dynamic Media: GIF or Image Carousel */}
        {hasDynamicMedia && (
          <motion.img
            key={mediaItems[currentImageIndex]}
            src={mediaItems[currentImageIndex]}
            alt={`${project.title} preview ${currentImageIndex}`}
            className={`absolute top-0 left-0 w-full h-full object-cover transition-opacity duration-300 ${isHovered ? "opacity-100" : "opacity-0"
              }`}
            initial={{ opacity: 0 }}
            animate={{ opacity: isHovered ? 1 : 0 }}
            transition={{ duration: 0.3 }}
          />
        )}

        {/* Gradient Overlay */}
        <div className={`absolute inset-0 bg-gradient-to-t ${isHovered ? "from-black/70 via-black/30 to-transparent" : "from-black/50 via-black/10 to-transparent"} z-10`} />
      </div>

      {/* Project Info Overlay */}
      <motion.div
        className="absolute inset-0 z-20 flex flex-col justify-end p-6"
        animate={{ opacity: isHovered ? 1 : 0 }}
        transition={{ duration: 0.3 }}
      >
        <h3 className="text-white text-2xl font-semibold">{project.title}</h3>
        <div className="mt-3 flex gap-3 flex-wrap">
          {project.tech.split(" / ").map((tech, i) => (
            <span
              key={i}
              className="inline-flex items-center text-xs text-white font-medium bg-black/30 backdrop-blur-sm px-3 py-1 rounded-full"
            >
              {tech}
            </span>
          ))}
        </div>

        <Link
          to={project.link}
          className="inline-flex items-center mt-4 text-sm text-blue-400 hover:text-blue-300 transition-colors"
        >
          View Project
          <svg
            className="ml-2 w-4 h-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M14 5l7 7m0 0l-7 7m7-7H3"
            />
          </svg>
        </Link>
      </motion.div>

      {/* Hover Scale Effect for the Card */}
      <motion.div
        className="absolute inset-0 rounded-3xl bg-white opacity-0 group-hover:opacity-100 transition-all duration-300 shadow-xl"
        animate={{ scale: isHovered ? 1.05 : 1 }}
        transition={{ type: "spring", stiffness: 250, damping: 20 }}
      />
    </motion.div>
  );
};

export default ProjectCard;